/**
 * Optimistic action queue (spec §7.3): `act()` writes an `ops` row, applies the change to the
 * cache and counters immediately, then `flushOps()` posts queued ops one at a time in id order
 * with the retry schedule [1 s, 4 s, 15 s]; permanent failures drop the op and resync. Undo
 * restores the snapshot taken by `applyLocal()` and, once the server has moved the messages,
 * enqueues a reverse move using the returned `uidMap`.
 *
 * Ordering: `ops.id` is auto-incremented, so "read, unread, read" on one uid flushes in call
 * order; `act()` itself runs through a serial queue so the local state is applied in order too.
 */
import { api, ApiError } from '$lib/api/client';
import { db, dbBackground, isDbGone, rowKey } from '$lib/db';
import type { I18nKey } from '$lib/i18n';
import type { MessageRow, OpName, OpRec } from '$lib/types';
import { serial } from '$lib/util/singleFlight';
import { plainFolder } from './list.svelte';
import type { MailStore } from './mail.svelte';
import type { UndoSlot } from './state.svelte';

export const OP_CHUNK = 500;
export const RETRY_MS: readonly number[] = Object.freeze([1000, 4000, 15000]);
export const MAX_TRIES = 4;
export const UNDO_RESYNC_MS = 2500;

export interface ActOptions {
  undoable?: boolean;
  toast?: I18nKey;
  label?: string;
  /** Internal: chunks after the first neither toast nor touch the undo slot. */
  silent?: boolean;
}

export interface LocalDelta {
  rows: MessageRow[];
  delta: { wasUnseen: number; wasSeen: number; count: number };
}

/** 4xx except 408/429, or 501 → never retried. */
export function isPermanentFailure(status: number): boolean {
  return (status >= 400 && status < 500 && status !== 408 && status !== 429) || status === 501;
}

export class OpsModule {
  private flushing = false;
  flushPromise: Promise<void> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Deferred resyncs, tracked so shutdown/teardown can cancel them before the DB closes. */
  private readonly deferred = new Set<ReturnType<typeof setTimeout>>();
  private disposed = false;
  private readonly enqueue = serial();
  /** opId → uidMap, capped at 20 (oldest 10 evicted). */
  readonly uidMaps = new Map<number, [number, number][] | null>();

  constructor(private readonly s: MailStore) {}

  /** Run `fn` later, tracking the timer so `dispose()` can cancel it. */
  private defer(fn: () => void | Promise<void>, ms: number): void {
    const t = setTimeout(() => {
      this.deferred.delete(t);
      // A closed database means the app (or the test) shut down between arming and firing.
      void dbBackground(async () => fn());
    }, ms);
    this.deferred.add(t);
  }

  /** Cancel every pending retry/resync and stop an in-flight flush (shutdown / teardown). */
  dispose(): void {
    this.disposed = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    for (const t of this.deferred) clearTimeout(t);
    this.deferred.clear();
  }

  /** Re-arm after a dispose (a new session in the same page, or the next test). */
  revive(): void {
    this.disposed = false;
  }

  rememberUidMap(opId: number, map: [number, number][] | null): void {
    this.uidMaps.set(opId, map);
    if (this.uidMaps.size > 20) for (const k of [...this.uidMaps.keys()].slice(0, 10)) this.uidMaps.delete(k);
  }

  /** Queue + optimistic apply. Chunks of 500; only the first chunk is undoable/toasted. */
  act(op: OpName, folder: string, uids: readonly number[], dest?: string, opts: ActOptions = {}): Promise<void> {
    return this.enqueue(() => this.actNow(op, folder, uids, dest, opts));
  }

  private async actNow(op: OpName, folder: string, uids: readonly number[], dest: string | undefined, opts: ActOptions): Promise<void> {
    this.s.lastLocalAct = Date.now();
    if (uids.length > OP_CHUNK) {
      for (let i = 0; i < uids.length; i += OP_CHUNK) {
        // Only the first chunk owns the toast and the undo slot; the rest must not clear them.
        await this.actNow(op, folder, uids.slice(i, i + OP_CHUNK), dest, i === 0 ? opts : { ...opts, undoable: false, toast: undefined, silent: true });
      }
      return;
    }
    const rec: OpRec = { op, folder, uids: [...uids], dest, label: opts.label, ts: Date.now(), state: 'queued', tries: 0 };
    const opId = await db.ops.add(rec);
    const local = await this.applyLocal(op === 'move' ? 'delete' : op, folder, uids, dest);
    const purge = op === 'delete' && folder === this.s.trashPath;
    const undoable = !!opts.undoable && !purge;
    if (undoable) {
      const slot: UndoSlot = { opId, op, folder, dest, ...local, uidMap: this.uidMaps.get(opId) };
      this.s.undoSlot = slot;
      this.s.undoToken = String(opId);
    } else if (!opts.silent) {
      this.s.undoSlot = null;
      this.s.undoToken = null;
    }
    void this.flushOps();
    if (opts.silent) return;
    const toast: I18nKey | null = this.s.offline
      ? 'queued'
      : opts.toast
        ? opts.toast
        : purge
          ? 'purgedToast'
          : op === 'delete'
            ? 'deletedToast'
            : op === 'move'
              ? 'movedToast'
              : undoable
                ? 'doneToast'
                : null;
    if (toast) this.s.showToast(toast, undoable);
  }

  /** Update cached rows + folder counters; returns the snapshot used by undo. */
  async applyLocal(op: OpName, folder: string, uids: readonly number[], dest?: string): Promise<LocalDelta> {
    let wasUnseen = 0;
    let wasSeen = 0;
    const rows: MessageRow[] = [];
    for (const uid of uids) {
      const key = rowKey(folder, uid);
      const row = await db.messages.get(key);
      if (!row) continue;
      rows.push(row);
      if (row.flags.includes('\\Seen')) wasSeen++;
      else wasUnseen++;
      const flags = new Set(row.flags);
      if (op === 'seen') flags.add('\\Seen');
      if (op === 'unseen') flags.delete('\\Seen');
      if (op === 'flag') flags.add('\\Flagged');
      if (op === 'unflag') flags.delete('\\Flagged');
      if (op === 'delete') {
        await db.messages.delete(key);
        await db.bodies.delete(key);
        continue;
      }
      await db.messages.put({ ...row, flags: [...flags] });
    }
    const src = this.s.folders.find((f) => f.path === folder);
    if (src) {
      if (op === 'seen') src.unseen = Math.max(0, (src.unseen ?? 0) - wasUnseen);
      else if (op === 'unseen') src.unseen = Math.max(0, (src.unseen ?? 0) + wasSeen);
      else if (op === 'delete') {
        src.unseen = Math.max(0, (src.unseen ?? 0) - wasUnseen);
        src.messages = Math.max(0, (src.messages ?? 0) - uids.length);
      }
    }
    const dst = dest ? this.s.folders.find((f) => f.path === dest) : undefined;
    if (dst) {
      dst.unseen = (dst.unseen ?? 0) + wasUnseen;
      dst.messages = (dst.messages ?? 0) + uids.length;
    }
    if (src || dst) {
      this.s.folders = [...this.s.folders];
      await db.folders.bulkPut([src, dst].filter((f): f is NonNullable<typeof f> => !!f).map(plainFolder));
    }
    if (this.s.currentFolder === folder) await this.s.loadListFromCache(folder);
    return { rows, delta: { wasUnseen, wasSeen, count: uids.length } };
  }

  /** Single-flight drain of the queue; skipped offline. */
  flushOps(): Promise<void> {
    if (this.flushing || this.s.offline) return this.flushPromise ?? Promise.resolve();
    this.flushing = true;
    this.flushPromise = (async () => {
      try {
        for (;;) {
          if (this.disposed) break;
          const op = await db.ops.orderBy('id').first();
          if (!op || this.s.offline || this.disposed) break;
          await db.ops.update(op.id!, { state: 'sending' });
          try {
            const res = await api.actions(op.folder, op.uids, op.op, op.dest, op.label);
            if ((op.op === 'label' || op.op === 'unlabel') && res && res.ok === false) throw new ApiError(502, 'label_failed');
            this.rememberUidMap(op.id!, res?.uidMap ?? null);
            await db.ops.delete(op.id!);
            const slot = this.s.undoSlot;
            if (slot && slot.opId === op.id) {
              slot.uidMap = res?.uidMap ?? null;
              // A move/delete without COPYUID cannot be reversed: hide the Undo button.
              if ((op.op === 'move' || op.op === 'delete') && !res?.uidMap) this.s.undoToken = null;
            }
          } catch (err) {
            const status = err instanceof ApiError ? err.status : 0;
            const tries = (op.tries ?? 0) + 1;
            if (isPermanentFailure(status) || tries >= MAX_TRIES) {
              await this.dropOp(op);
              continue;
            }
            await db.ops.update(op.id!, { state: 'queued', tries });
            this.scheduleRetry(RETRY_MS[tries - 1] ?? 15000);
            break;
          }
        }
      } catch (e) {
        // The only thing that can throw out here is Dexie. A closed handle means the app or
        // the test shut down mid-flush: stop quietly rather than reject a `void`-ed promise.
        if (!isDbGone(e)) throw e;
      } finally {
        this.flushing = false;
      }
    })();
    return this.flushPromise;
  }

  private scheduleRetry(ms: number): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void dbBackground(() => this.flushOps());
    }, ms);
  }

  /** Permanent failure: delete the op, clear undo, toast, resync folders + folder. */
  async dropOp(op: OpRec): Promise<void> {
    await db.ops.delete(op.id!);
    if (this.s.undoSlot?.opId === op.id) {
      this.s.undoSlot = null;
      this.s.undoToken = null;
    }
    this.s.showToast('actionFailed');
    try {
      await this.s.refreshFolders();
      await this.s.syncFolder(op.folder);
    } catch {
      /* the next refresh will catch up */
    }
  }

  async pendingCount(): Promise<number> {
    return db.ops.count();
  }

  /** Undo the last undoable action (spec §7.3 `undo()`). */
  async undo(): Promise<void> {
    const slot = this.s.undoSlot;
    if (!slot) return;
    this.s.undoSlot = null;
    this.s.undoToken = null;
    this.s.lastLocalAct = Date.now();
    if ((await db.ops.get(slot.opId))?.state === 'sending') await this.flushPromise?.catch(() => undefined);
    const queued = await db.ops.get(slot.opId);
    if (queued) {
      // Never reached the server (or is being retried): drop it and put the rows back.
      await db.ops.delete(slot.opId);
      await this.restoreSnapshot(slot);
      this.s.showToast('undoneToast');
      if ((queued.tries ?? 0) > 0 || queued.state === 'sending') {
        this.defer(() => this.s.syncFolder(slot.folder).catch(() => undefined), 1200);
      }
      return;
    }
    const newUids = (slot.uidMap ?? this.uidMaps.get(slot.opId) ?? []).map(([, n]) => n);
    if (!slot.dest && slot.op !== 'delete') {
      this.s.showToast('undoUnavailable');
      return;
    }
    const from = slot.dest ?? this.s.trashPath;
    if (!newUids.length || !from) {
      this.s.showToast('undoUnavailable');
      return;
    }
    await this.restoreSnapshot(slot);
    await db.ops.add({ op: 'move', folder: from, uids: newUids, dest: slot.folder, ts: Date.now(), state: 'queued', tries: 0, undoOf: slot.opId });
    void this.flushOps();
    this.s.showToast('undoneToast');
    this.defer(async () => {
      // The restored rows carry the old uids; after the reverse move the server assigns new ones.
      await db.messages.bulkDelete(slot.rows.map((r) => r.key));
      await db.bodies.bulkDelete(slot.rows.map((r) => r.key));
      await this.s.syncFolder(slot.folder).catch(() => undefined);
    }, UNDO_RESYNC_MS);
  }

  /** Put snapshot rows back and reverse the counter changes of `applyLocal()`. */
  async restoreSnapshot(slot: UndoSlot): Promise<void> {
    await db.messages.bulkPut(slot.rows);
    const src = this.s.folders.find((f) => f.path === slot.folder);
    const dst = slot.dest ? this.s.folders.find((f) => f.path === slot.dest) : undefined;
    const { wasUnseen, wasSeen, count } = slot.delta;
    if (src) {
      if (slot.op === 'seen') src.unseen = Math.max(0, (src.unseen ?? 0) + wasUnseen);
      else if (slot.op === 'unseen') src.unseen = Math.max(0, (src.unseen ?? 0) - wasSeen);
      else {
        src.unseen = Math.max(0, (src.unseen ?? 0) + wasUnseen);
        src.messages = Math.max(0, (src.messages ?? 0) + count);
      }
    }
    if (dst && (slot.op === 'move' || slot.op === 'delete')) {
      dst.unseen = Math.max(0, (dst.unseen ?? 0) - wasUnseen);
      dst.messages = Math.max(0, (dst.messages ?? 0) - count);
    }
    this.s.folders = [...this.s.folders];
    const touched = [src, dst].filter((f): f is NonNullable<typeof f> => !!f);
    if (touched.length) await db.folders.bulkPut(touched.map(plainFolder));
    if (this.s.currentFolder === slot.folder) await this.s.loadListFromCache(slot.folder);
  }

  dismissUndo(): void {
    this.s.undoSlot = null;
    this.s.undoToken = null;
  }
}
